import { Link, useNavigate, useParams } from 'react-router-dom';
import { LinkIcon } from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { MissingItem } from '../ui/MissingItem';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { Toggle } from '../ui/Toggle';
import { TagInput } from '../ui/TagInput';
import { ChipSelect } from '../ui/ChipSelect';
import { ImageField } from '../ui/ImageField';
import { ProductPicker } from '../ui/ProductPicker';
import { DeleteButton } from '../ui/DeleteButton';
import { deleteBlockedReason } from './catalogLinks';
import { undoToast } from '../ui/undoToast';
import { CheckList } from '../ui/CheckList';
import { NumberInput } from '../ui/NumberInput';
import { ViewOnSite } from './ViewOnSite';
import { PromoPanel } from './PromoPanel';
import { ProductMarginPanel } from './ProductMarginPanel';
import { ProductMediaPanel } from './ProductMediaPanel';
import { StockField } from './StockField';
import { UsageFields } from './UsageFields';
import { useEditorItem } from './useEditorItem';
import { useAdmin } from '../AdminContext';
import { SeoPanel } from '../ui/SeoPanel';
import { productSeoChecks } from './catalogSeo';
import { productSeoAuto } from '../../utils/seoAuto';
import { skinTypes } from '../../data/concerns';
import { PRODUCT_NOTE_MAX, TAG_SUGGESTIONS, emptyProduct, oneOf, productCategories, productChecks, routineSteps, uniqueSorted } from './catalogRules';
import { btnPrimary, inputCls } from '../ui/styles';
import { slugify, uniqueSlug } from '../editing';
import { euro } from '../../core/format/currency';
import { plural } from '../../core/format/text';
const back = {
  to: '/be/producten',
  label: 'Alle producten'
};

/** Eén product: basis, huidthema's, teksten, koppelingen en voorraad. */
export function ProductEditor() {
  const {
    id = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    catalog,
    index,
    site,
    putItem,
    removeItem
  } = useAdmin();
  const {
    isNew,
    item: product,
    set
  } = useEditorItem('products', id, emptyProduct);
  if (!product) return <MissingItem title="Product niet gevonden" back={back} data-id="element-670" data-mp="15o6gycq114go" />;
  const takenIds = catalog.products.map(p => p.id);
  const suggestedId = uniqueSlug(slugify(`${product.brand} ${product.name}`), takenIds);
  // Een zelf getypt webadres dat al bestaat zou dat andere product overschrijven.
  const idTaken = isNew && !!product.id && takenIds.includes(product.id);
  const canAdd = product.name.trim() !== '' && product.price > 0 && !idTaken;
  const checks = [...productChecks(product), ...productSeoChecks(product, catalog.products)];
  const inRoutines = index.routinesWithProduct(product.id);
  const inArticles = catalog.articles.filter(a => a.productIds?.includes(product.id));
  // Verwijderen haalt hem ook uit protocollen en journalstukken; een protocol kan dan onder de twee zakken.
  const usedIn = [inRoutines.length ? plural(inRoutines.length, 'protocol', 'protocollen') : '', inArticles.length ? plural(inArticles.length, 'journalstuk', 'journalstukken') : ''].filter(Boolean);
  const thinRoutines = inRoutines.filter(r => r.productIds.length <= 2);
  // Als cadeau gekoppeld: dan zou de staffel naar een product wijzen dat niet meer bestaat. Eerst ontkoppelen.
  const giftLinks = site.giftTiers.filter(t => t.stockItemId === product.id || Object.values(t.choiceStockItems ?? {}).includes(product.id)).map(t => `cadeau “${t.title}”`);
  const concerns = index.concernsForProduct(product);
  const allIngredients = uniqueSorted(catalog.products.map(p => p.keyIngredients));
  const add = () => {
    if (!canAdd) return;
    const created = {
      ...product,
      id: product.id ? uniqueSlug(product.id, takenIds) : suggestedId
    };
    putItem('products', created);
    navigate(`/be/producten/${created.id}`, {
      replace: true
    });
  };
  return <div data-id="element-671" data-mp="132yzzv0u6hfr">
      <PageHeader title={isNew ? 'Nieuw product' : product.name || 'Naamloos product'} description={isNew ? 'Vul in wat je weet — lege velden verbergt de site vanzelf.' : `${product.brand} · ${product.category}`} back={back} actions={isNew ? <span className="flex flex-wrap items-center justify-end gap-3" data-id="element-673" data-mp="3vx7aizhk9ey3">
              {!canAdd && <span className="text-[13px] text-muted" data-id="element-674" data-mp="3hy7lhqq4yy1e">
                  {!product.name.trim() ? 'Vul een naam in' : !(product.price > 0) ? 'Vul een prijs in' : 'Dit webadres bestaat al'}
                </span>}
              <button type="button" onClick={add} disabled={!canAdd} className={btnPrimary} data-id="element-675" data-mp="38736ws4dhf73">
                Product toevoegen
              </button>
            </span> : <>
              <DeleteButton undoable blockedReason={deleteBlockedReason(giftLinks, 'Kies in Instellingen › Cadeaus & codes eerst een ander artikel.')} warning={`${usedIn.length ? `Zit in ${usedIn.join(' en ')}. Wordt daar ook weggehaald.${thinRoutines.length ? ` ${thinRoutines.map(r => r.name).join(', ')} houdt dan minder dan twee producten over.` : ''} ` : ''}Staat het in een abonnement, dan gaat het niet meer mee in de doos. Liever tijdelijk uit de winkel? Zet de voorraad op 0.`} onConfirm={() => {
        const restore = removeItem('products', product.id);
        navigate(back.to);
        undoToast(`“${product.name || 'Naamloos product'}” verwijderd`, restore, {
          description: usedIn.length ? `Ongedaan maken zet hem ook terug in ${usedIn.join(' en ')}.` : 'Pas definitief als je opslaat.',
          restored: 'Product teruggezet'
        });
      }} data-id="element-676" data-mp="2z6erbszbz6s3" />
              <ViewOnSite path={`/webshop/${product.id}`} data-id="element-677" data-mp="3db9ke1nkfk0d" />
            </>} data-id="element-672" data-mp="132p4jrx0vi2o" />

      {/* Eén foto-paneel, door het grid geplaatst: op mobiel bovenaan (daar herken je het product
          aan), vanaf lg bovenaan de zijkolom. De `1fr`-rij vangt de hoogte van de lange formulierkolom
          op, zodat er geen gat valt tussen de foto en de rest van de zijkolom. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:grid-rows-[auto_1fr]" data-id="element-678" data-mp="3pc7t5fzq1eat">
        <Panel title="Productfoto" description="Een packshot op wit of transparant werkt het best." className="lg:col-span-4 lg:col-start-9 lg:row-start-1" data-id="element-679" data-mp="3u2y190zj7v97">
          <div className="mx-auto max-w-[14rem] lg:max-w-none" data-id="element-680" data-mp="6y8cop3596rj">
            <ImageField value={product.image} onChange={image => set({
            image
          })} data-id="element-681" data-mp="70l3lrqvqrgq" />
          </div>
        </Panel>

        <div className="min-w-0 space-y-6 lg:col-span-8 lg:col-start-1 lg:row-span-2 lg:row-start-1" data-id="element-682" data-mp="gwo3ss268g11">
          <Panel title="Basis" data-id="element-683" data-mp="2doy23s8ig9n0">
            <div className="grid gap-5 sm:grid-cols-2" data-id="element-684" data-mp="2dpr82qznav43">
              <Field label="Naam" className="sm:col-span-2" data-id="element-685" data-mp="2drat0uw8xuc7">
                <input value={product.name} onChange={e => set({
                name: e.target.value
              })} className={inputCls} placeholder="Bijv. Crystal Retinal 1" data-id="element-686" data-mp="2iou8tz8lef69" />
              </Field>
              <Field label="Merk" data-id="element-687" data-mp="26djm0gr6mjpl">
                <select value={product.brand} onChange={e => set({
                brand: e.target.value
              })} className={inputCls} data-id="element-688" data-mp="26cibcde2vz8e">
                  {catalog.brands.map(b => <option key={b.name} data-id="element-689" data-mp="1wgf1elcziwaw">{b.name}</option>)}
                </select>
              </Field>
              <Field label={product.promo ? 'Actieprijs (€, incl. btw)' : 'Prijs (€, incl. btw)'} hint={product.promo ? 'Pas je aan bij Leveranciersactie.' : 'Adviesprijs van het merk, nooit korting.'} data-id="element-690" data-mp="2u2vufeeoqnqu">
                <NumberInput decimals value={product.price || undefined} onChange={price => set({
                price: price ?? 0
              })} emptyMessage="Vul een prijs in, bijv. 49,95." placeholder="49,95" data-id="element-691" data-mp="2f6dg3kbafnni" />
              </Field>
              <Field label="Soort product" data-id="element-692" data-mp="1teu91rk34710">
                <select value={product.category} onChange={e => {
                const category = oneOf(productCategories, e.target.value);
                if (category) set({
                  category
                });
              }} className={inputCls} data-id="element-693" data-mp="1tdsomwh6i86m">
                  {productCategories.map(c => <option key={c} data-id="element-694" data-mp="2c6mt2wh6ehwl">{c}</option>)}
                </select>
              </Field>
              <Field label="Stap in de routine" hint="Bepaalt de volgorde en het filter in de webshop." data-id="element-695" data-mp="2h3d9jkww7fij">
                <select value={product.step ?? ''} onChange={e => set({
                step: oneOf(routineSteps, e.target.value)
              })} className={inputCls} data-id="element-696" data-mp="23cruy8eh1rqg">
                  <option value="" data-id="element-697" data-mp="2v4pmdngrb107">{product.step ? 'Geen stap' : 'Kies een stap'}</option>
                  {routineSteps.map(s => <option key={s} data-id="element-698" data-mp="2zw7km3reanhh">{s}</option>)}
                </select>
              </Field>
              <Field label="Inhoud" hint="Bijv. 30 ml. Leeg = niet getoond." data-id="element-699" data-mp="oem9elh8f6yz">
                <input value={product.volume ?? ''} onChange={e => set({
                volume: e.target.value || undefined
              })} className={inputCls} placeholder="30 ml" data-id="element-700" data-mp="jg2ngl2n1v6m" />
              </Field>
              {product.kind === 'giftcard' ? <Field label="Geldigheid" hint="Bijv. 12 maanden geldig." data-id="element-701" data-mp="t7p632hb2wje">
                  <input value={product.usesPerBottle ?? ''} onChange={e => set({
                usesPerBottle: e.target.value || undefined
              })} className={inputCls} data-id="element-702" data-mp="5ie4jis4sy47" />
                </Field> : <UsageFields product={product} set={set} data-id="element-703" data-mp="fgjzthjfn603" />}
            </div>
          </Panel>

          <Panel title="Huidthema’s" description="Hiermee verschijnt het product in de filters, de klachtenkiezer en het huidadvies." data-id="element-704" data-mp="afmnvmp0n871">
            <ChipSelect options={skinTypes} value={product.skinTypes} onChange={next => set({
            skinTypes: next
          })} meta={s => String(index.productsForSkinType(s).filter(p => p.id !== product.id).length)} data-id="element-705" data-mp="1qwigo7kuqwbv" />
            {concerns.length > 0 && <p className="mt-4 flex items-start gap-2 text-[13px] leading-relaxed text-muted" data-id="element-706" data-mp="27c7bbzxecim3">
                <LinkIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-707" data-mp="22dnnftz8swr3" />
                Wordt aanbevolen bij: {concerns.map(c => c.label.toLowerCase()).join(', ')}.
              </p>}
          </Panel>

          <Panel title="Teksten" description="In je eigen woorden — zo staat het op de productpagina." data-id="element-708" data-mp="1tbedp6bst7er">
            <div className="space-y-5" data-id="element-709" data-mp="1oduya33d6ol8">
              <Field label="Korte omschrijving" hint="Eén zin onder de naam, op de productkaart." count={{
              value: product.note.length,
              max: PRODUCT_NOTE_MAX
            }} data-id="element-710" data-mp="1jf1mcf3q75n6">
                <input value={product.note} onChange={e => set({
                note: e.target.value
              })} className={inputCls} data-id="element-711" data-mp="3ipjioksd7jd0" />
              </Field>
              <Field label="Waarom ik dit kies" hint="Je persoonlijke verhaal: voor wie, wat je ziet in de salon." data-id="element-712" data-mp="3lcllcj0ni2j2">
                <textarea value={product.why} onChange={e => set({
                why: e.target.value
              })} rows={5} className={`${inputCls} resize-y leading-relaxed`} data-id="element-713" data-mp="3qadf7ikuvypw" />
              </Field>
              <Field label="Zo gebruik je het" data-id="element-714" data-mp="3vaqaq8k8xff">
                <textarea value={product.howTo} onChange={e => set({
                howTo: e.target.value
              })} rows={3} className={`${inputCls} resize-y leading-relaxed`} data-id="element-715" data-mp="3v1134vcauas" />
              </Field>
              <Field label="Belangrijkste ingrediënten" as="div" data-id="element-716" data-mp="37dc4dh4mf4e1">
                <TagInput label="Belangrijkste ingrediënten" value={product.keyIngredients} onChange={keyIngredients => set({
                keyIngredients
              })} suggestions={allIngredients.filter(i => !product.keyIngredients.includes(i)).slice(0, TAG_SUGGESTIONS)} data-id="element-717" data-mp="3cbndfpn3dav7" />
              </Field>
            </div>
          </Panel>

          <ProductMediaPanel value={product.media ?? []} onChange={media => set({
          media
        })} data-id="element-718" data-mp="2sn4bfjuecgr6" />

          <Panel title="Adviseer ik hierbij" description="Verschijnt als “Samen versterken ze elkaar” op de productpagina en in de winkelmand." data-id="element-719" data-mp="2nojk69x4h7ev">
            <ProductPicker value={product.pairs} onChange={pairs => set({
            pairs
          })} products={catalog.products} exclude={[product.id]} emptyText="Nog geen producten die je hierbij adviseert." data-id="element-720" data-mp="31mra3xvqr8gj" />
          </Panel>

          {product.kind !== 'giftcard' && <SeoPanel seo={product.seo} onChange={seo => set({
          seo
        })} auto={productSeoAuto(product)} path={product.id ? `/webshop/${product.id}` : '/webshop'} imageFallback="de productfoto" data-id="element-721" data-mp="2ip6m0t0swdwz" />}
        </div>

        <aside aria-label="Actie, controle en zichtbaarheid" className="min-w-0 space-y-6 lg:col-span-4 lg:col-start-9 lg:row-start-2" data-id="element-722" data-mp="1m7ac8n5mfr77">
          <PromoPanel product={product} set={set} data-id="element-723" data-mp="1c2c5lco90o2v" />

          {!isNew && <ProductMarginPanel product={product} data-id="element-724" data-mp="1gu3pj4cwfvk1" />}

          <Panel title="Wat mist er nog?" data-id="element-725" data-mp="1gvn32n767si4">
            <CheckList checks={checks} okText="Dit product is compleet" data-id="element-726" data-mp="1257jfcueksm2" />
          </Panel>

          <Panel title="Zichtbaarheid" data-id="element-727" data-mp="123cxbv7eauge">
            <div className="space-y-1" data-id="element-728" data-mp="175bz50nhljb8">
              <Toggle checked={Boolean(product.bestseller)} onChange={bestseller => set({
              bestseller
            })} label="Bestseller" description="Op de homepage in “Liever met één product beginnen?”." data-id="element-729" data-mp="1726a1leuyhfi" />
              <Toggle checked={Boolean(product.salonFavourite)} onChange={salonFavourite => set({
              salonFavourite
            })} label="Favoriet uit de salon" description="Zelfde plekken op de homepage als een bestseller. Je eigen plank op Over Sarah kies je los, in Paginateksten › Over Sarah." data-id="element-730" data-mp="1kuvzjztli4hd" />
              <Toggle checked={product.refill} onChange={refill => set({
              refill
            })} label="Automatisch bij te vullen" description="Klant kan het in het Bewust Abonnement zetten." data-id="element-731" data-mp="un9ljdiasm50" />
            </div>
          </Panel>

          {inRoutines.length > 0 && <Panel title="Zit in protocollen" data-id="element-732" data-mp="fzeq3pcwo3o8">
              <ul className="space-y-2 text-[14px]" data-id="element-733" data-mp="1dlc3j8cfhitb">
                {inRoutines.map(r => <li key={r.id} data-id="element-734" data-mp="18mixu23duw3p">
                    <Link to={`/be/protocollen/${r.id}`} className="text-ink underline-offset-4 hover:text-clay hover:underline" data-id="element-735" data-mp="18nkecm364ast">
                      {r.name}
                    </Link>
                    <span className="text-muted" data-id="element-736" data-mp="yp4ik5t74dcz"> · {euro(index.routineTotals(r).price)}</span>
                  </li>)}
              </ul>
              <p className="mt-3 text-[12px] text-muted" data-id="element-737" data-mp="3obkftct6f6u9">De protocolprijs past zich vanzelf aan je prijs aan.</p>
            </Panel>}

          <Panel title="Koppeling & voorraad" data-id="element-738" data-mp="1yxb039c78sh2">
            <div className="space-y-4" data-id="element-739" data-mp="1yugg1ix3l9o6">
              <Field label="Webadres" hint={idTaken ? 'Dit webadres is al van een ander product. Kies een ander.' : isNew ? 'Wordt vanzelf gemaakt uit merk en naam.' : 'Staat vast, zodat oude links en Google blijven werken.'} data-id="element-740" data-mp="23v34h2u16725">
                <input value={isNew ? product.id || suggestedId : product.id} onChange={e => isNew && set({
                id: slugify(e.target.value)
              })} disabled={!isNew} className={`${inputCls} font-mono text-[13px]`} data-id="element-741" data-mp="2mlkswv7tyghp" />
              </Field>
              <Field label="Omschrijving van de foto" hint="Voor blinde bezoekers en Google. Leeg = merk en naam." data-id="element-742" data-mp="x87katsoq6as">
                <input value={product.imageAlt ?? ''} onChange={e => set({
                imageAlt: e.target.value || undefined
              })} className={inputCls} data-id="element-743" data-mp="12723vxyh7yvy" />
              </Field>
              <Field label="Barcode (EAN)" hint="Staat op de doos. Helpt Google Shopping." data-id="element-744" data-mp="q4hhmhoekpl8">
                <input value={product.gtin ?? ''} onChange={e => set({
                gtin: e.target.value.replace(/\D/g, '') || undefined
              })} inputMode="numeric" maxLength={14} className={`${inputCls} font-mono text-[13px]`} data-id="element-745" data-mp="143h9dy77pklm" />
              </Field>
              {isNew ? <Field label="Beginvoorraad" hint="Leeg = niet bijhouden. Daarna pas je de voorraad hier los aan." data-id="element-746" data-mp="190r49tddeogu">
                  <NumberInput allowEmpty value={product.stock} onChange={stock => set({
                stock
              })} placeholder="Niet bijhouden" data-id="element-747" data-mp="1hv5gfn2ltivq" />
                </Field> : <StockField key={product.id} productId={product.id} stock={product.stock} data-id="element-748" data-mp="1wqbp6ht33vsr" />}
              {/* Technisch nummer: vult de koppeling zelf in. Alleen tonen als het er is, zodat niemand het met de hand gaat typen. */}
              {product.wooId !== undefined && <p className="text-[12px] text-muted" data-id="element-749" data-mp="2ffx9mh0cmh69">
                  WooCommerce-nummer <span className="font-mono text-ink" data-id="element-750" data-mp="21g5ozxfbu2ja">{product.wooId}</span>
                </p>}
            </div>
          </Panel>
        </aside>
      </div>
    </div>;
}